<template>
  <div
    v-if="
      links?.length ||
      currentReports?.length ||
      showAddReport ||
      !presentationMode
    "
    class="tw-flex tw-flex-col tw-gap-2"
    :class="{
      'tw-px-6 tw-py-4 tw-rounded-lg tw-group tw-bg-sf-snow-lighter':
        presentationMode,
    }"
  >
    <template v-if="adding">
      <span class="tw-font-medium tw-text-base tw-text-sf-neutral-darker-1">
        {{ copy.quickLinks }}
      </span>
      <div
        class="
          tw-flex
          tw-flex-row
          tw-gap-8
          tw-p-4
          tw-rounded-lg
          tw-bg-sf-snow-lighter
          tw-shadow-md
        "
      >
        <div class="tw-grow tw-flex tw-flex-row tw-gap-4">
          <span class="tw-grow">
            <SfInput
              id="client-meeting-link-title"
              v-model="newTitle"
              data-testid="client-meeting-link-title-input"
              :label="copy.title"
              :placeholder="copy.addATitle"
              :has-error="newTitleError"
              required
            />
          </span>
          <span class="tw-grow-[2]">
            <SfInput
              v-model="newUrl"
              data-testid="client-meeting-link-url-input"
              :label="copy.url"
              :placeholder="copy.pasteLinkUrl"
              :has-error="newUrlError"
              required
            />
          </span>
        </div>
        <div class="tw-grow-0 tw-flex tw-flex-row tw-gap-4 tw-items-end">
          <SfButtonDs
            icon-only
            icon-before="close"
            variant="secondary"
            @click="stopAdding"
          />
          <SfButtonDs
            data-testid="client-meeting-link-save"
            icon-only
            icon-before="check"
            variant="primary"
            :disabled="savingLink"
            @click="saveLink"
          />
        </div>
      </div>
    </template>
    <div
      v-else
      class="
        tw-flex tw-flex-row tw-flex-wrap tw-gap-x-4 tw-gap-y-2 tw-items-center
      "
    >
      <div
        v-if="currentReports.length > 0 || showAddReport"
        data-walkthrough-id="meeting-reports-section"
        class="
          tw-flex tw-flex-row tw-gap-x-4 tw-gap-y-2 tw-flex-wrap tw-items-center
        "
      >
        <div class="tw-font-medium tw-text-sf-neutral-darker-1">
          {{ copy.reports }}
        </div>
        <LinksPill
          v-for="report in currentReports"
          :key="report.id"
          :title="report.title"
          :href="report.url"
          max-width="medium"
        >
          <template v-if="report.removable && editable" #suffix>
            <button
              :data-testid="`client-meeting-report-${report.id}-remove`"
              class="
                tw-text-xs
                tw-bg-transparent
                tw-border-0
                tw-p-0
                tw-cursor-pointer
              "
              @click="confirmRemoveReport(report)"
            >
              <i class="fa fa-close" />
            </button>
          </template>
        </LinksPill>
        <SfDropdown
          v-if="showAddReport"
          dropdown-id="add-report-dropdown"
          :is-open="reportDropdownOpen"
          :is-disabled="addReportDisabled"
          trigger-size="extra-small"
          @dropdown-trigger-clicked="toggleReportDropdown"
          @dropdown-closed="closeReportDropdown"
        >
          <template #trigger>
            <span
              class="tw-text-sf-neutral"
              data-testid="client-meeting-report-add"
            >
              <i class="fa fa-plus" />
              {{ copy.addReport }}
            </span>
          </template>
          <template #menu>
            <div class="tw-min-w-[350px]">
              <div class="tw-p-2">
                <SfSearch
                  v-model="reportSearchValue"
                  :show-search-icon="true"
                  :show-clear-icon="true"
                  :show-result-navigation="false"
                  :placeholder="copy.searchReports"
                  :bottom-margin="false"
                  :focused-at="searchFocusedAt"
                  @clear="reportSearchValue = ''"
                />
              </div>
              <div class="tw-overflow-y-auto tw-max-h-[300px]">
                <SfDropdownItem
                  v-for="item in filteredAvailableReports"
                  :key="item.id"
                  :has-border="false"
                  :is-disabled="isReportInFlight(item.id)"
                  :data-testid="`client-meeting-report-option-${item.id}`"
                  @click="addReport(item)"
                >
                  {{ item.title }}
                </SfDropdownItem>
                <div
                  v-if="filteredAvailableReports.length === 0"
                  class="tw-px-4 tw-py-3 tw-text-sf-neutral tw-text-center"
                >
                  {{ copy.noReportsFound }}
                </div>
              </div>
            </div>
          </template>
        </SfDropdown>
      </div>
      <div
        v-if="links.length > 0 || !presentationMode"
        class="
          tw-flex tw-flex-row tw-gap-x-4 tw-gap-y-2 tw-flex-wrap tw-items-center
        "
      >
        <div
          v-if="displayTitle"
          class="tw-font-medium tw-text-sf-neutral-darker-1"
        >
          {{ copy.quickLinks }}
        </div>
        <LinksPill
          v-for="link in links"
          :key="link.id"
          :title="link.title"
          :href="link.url"
          max-width="large"
          variant="primary"
          open-in-new-tab
        >
          <template #suffix>
            <i v-if="presentationMode" class="fa fa-external-link tw-text-xs" />
            <button
              v-if="editable"
              :data-testid="`client-meeting-link-${link.id}-remove`"
              class="
                tw-text-xs
                tw-text-sf-neutral
                tw-bg-transparent
                tw-border-0
                tw-p-0
                tw-cursor-pointer
              "
              @click="removeLink(link.id)"
            >
              <i class="fa fa-close" />
            </button>
          </template>
        </LinksPill>
        <SfButtonDs
          v-if="editable"
          data-testid="client-meeting-link-add"
          variant="secondary"
          size="sm"
          @click="startAdding"
        >
          <span class="tw-text-sf-neutral">
            <i class="fa fa-plus" />
            {{ copy.addLink }}
          </span>
        </SfButtonDs>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'
import { ref, toRef, watch } from 'vue'

import * as LinksApi from '@app/application/client_meetings/api/links'
import useI18n from '@app/shared/vue/composables/use-I18n'
import type {
  Link,
  Report,
  AvailableReport,
} from '@app/application/client_meetings/types'
import useMeetingReports from '@app/application/client_meetings/screens/prepare/composables/use-meeting-reports'
import { SfButtonDs, SfInput } from '@silverfin/ui-components'
import {
  SfDropdown,
  SfDropdownItem,
} from '@app/shared/vue/components/sf-dropdown'
import SfSearch from '@app/shared/vue/components/sf-search/search-input.vue'
import {
  getPageTitleViaBrowser,
  getValidUrl,
} from '@app/application/client_meetings/screens/prepare/utils'
import LinksPill from '@app/application/client_meetings/screens/prepare/components/links-pill.vue'

const I18n = useI18n()
const copy = {
  quickLinks: I18n.t('client_meetings.prepare.links.quick_links'),
  title: I18n.t('client_meetings.prepare.links.title'),
  addATitle: I18n.t('client_meetings.prepare.links.add_a_title'),
  url: I18n.t('client_meetings.prepare.links.url'),
  pasteLinkUrl: I18n.t('client_meetings.prepare.links.paste_link_url'),
  reports: I18n.t('client_meetings.prepare.links.reports'),
  addReport: I18n.t('client_meetings.prepare.links.add_report'),
  searchReports: I18n.t('client_meetings.prepare.links.search_reports'),
  noReportsFound: I18n.t('client_meetings.prepare.links.no_reports_found'),
  confirmRemoveReport: I18n.t(
    'client_meetings.prepare.links.confirm_remove_report'
  ),
  addLink: I18n.t('client_meetings.prepare.links.add_link'),
}

interface Props {
  reports?: Report[]
  availableReports?: AvailableReport[]
  links: Link[]
  editable?: boolean
  displayTitle?: boolean
}

const props = defineProps<Props>()

const presentationMode = window.Silverfin.presentationMode

const {
  currentReports,
  filteredAvailableReports,
  reportSearchValue,
  reportDropdownOpen,
  searchFocusedAt,
  showAddReport,
  addReportDisabled,
  isReportInFlight,
  toggleReportDropdown,
  closeReportDropdown,
  addReport,
  removeReport,
} = useMeetingReports(
  props.reports ?? [],
  props.availableReports ?? [],
  toRef(() => props.editable)
)

const savingLink = ref<boolean>(false)
const adding = ref<boolean>(false)
const links = ref<Link[]>(props.links)
const newUrl = ref<string>('')
const newUrlError = ref<boolean>(false)
const newTitle = ref<string>('')
const newTitleError = ref<boolean>(false)

function startAdding() {
  adding.value = true
}

const prefillTitle = debounce(async () => {
  if (newTitle.value || !newUrl.value) {
    return
  }
  const validUrl = getValidUrl(newUrl.value)

  if (validUrl) {
    newUrl.value = validUrl
  }

  newUrlError.value = !validUrl

  if (!validUrl) {
    return
  }

  try {
    const url = new URL(newUrl.value)
    const result =
      url.hostname === window.location.hostname
        ? await getPageTitleViaBrowser(url)
        : await getPageTitleViaBackend(url)
    newTitle.value ||= result || ''
    newTitleError.value = false
  } catch {
    // Ignore
  }
}, 500)

watch(newUrl, prefillTitle)

function stopAdding() {
  adding.value = false
  newUrlError.value = false
  newTitleError.value = false
  newUrl.value = ''
  newTitle.value = ''
}

async function getPageTitleViaBackend(url: URL): Promise<string | null> {
  return (await LinksApi.fetchPageTitle(url.toString()))?.title ?? null
}

async function saveLink() {
  try {
    savingLink.value = true

    const validUrl = getValidUrl(newUrl.value)

    if (validUrl) {
      newUrl.value = validUrl
    }

    newUrlError.value = !validUrl
    newTitleError.value = !newTitle.value

    if (newTitleError.value || newUrlError.value) {
      return
    }
    const addedLink = await LinksApi.create(newTitle.value, newUrl.value)
    if (!addedLink) {
      return
    }
    links.value.push(addedLink)
    stopAdding()
  } finally {
    savingLink.value = false
  }
}

function confirmRemoveReport(report: Report) {
  window.sfConfirm({
    question: copy.confirmRemoveReport,
    onConfirm: () => removeReport(report),
  })
}

async function removeLink(id: number) {
  window.sfConfirm({
    question: I18n.t('client_meetings.prepare.links.confirm_remove'),
    onConfirm: async () => {
      await LinksApi.delete(id)
      links.value = links.value.filter((link: Link) => link.id !== id)
    },
  })
}
</script>
